import { SectionDividerLabel } from "@/components/common/section-components/SectionDividerLabel";
import { AppColors } from "@/styles/colors";
import { Flex, Grid, GridItem, Text } from "@chakra-ui/react";
import { useMemo } from "react";
import { Control, FieldValues, useWatch } from "react-hook-form";
import { calcGrossMarginCultivo, parseFormValue } from "../schema/helper-functions";
import { CurrencyFormField } from "./components/CurrencyFormField";
import { NumericCommaFormField } from "./components/NumericCommaFormField";
import { WaitingField } from "./components/WaitingField";
import { TipoCampoDisplay } from "@/constants/enums";

interface Props {
  index: number;
  control: Control<FieldValues>;
  granoNombre: string;
  establecimiento: string;
  tipoCampo: string;
  rindeIndicado: number;
  disabled?: boolean;
}

const fmt = (n: number, decimals = 2) =>
  Number.isFinite(n) ? n.toFixed(decimals) : "-";

const fmtDisplay = (n: number) =>
  Number.isFinite(n)
    ? new Intl.NumberFormat("es-AR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(n)
    : "-";

export const GrossMarginCultivoTab = ({
  index,
  control,
  granoNombre,
  establecimiento,
  tipoCampo,
  rindeIndicado,
  disabled,
}: Props) => {
  const prefix = `cultivos.${index}` as const;

  const watched = useWatch({
    control,
    name: [
      `${prefix}.rindeEsperado`,
      `${prefix}.cotizacionDolar`,
      `${prefix}.precioCereal`,
      `${prefix}.precioAgroquimicos`,
      `${prefix}.precioFertilizantes`,
      `${prefix}.precioSemillas`,
      `${prefix}.precioLabores`,
      `${prefix}.precioSeguros`,
      `${prefix}.precioAlquiler`,
      `${prefix}.gastoDeCosecha`,
    ],
  });

  const [
    rindeRaw,
    cotizacionDolarRaw,
    precioCerealRaw,
    agroquimicosRaw,
    fertilizantesRaw,
    semillasRaw,
    laboresRaw,
    segurosRaw,
    alquilerRaw,
    gastoCosechaRaw,
  ] = watched;

  const calc = useMemo(
    () =>
      calcGrossMarginCultivo({
        precioCereal: parseFormValue(precioCerealRaw),
        cotizacionDolar: parseFormValue(cotizacionDolarRaw),
        rindeEsperado: parseFormValue(rindeRaw),
        precioAgroquimicos: parseFormValue(agroquimicosRaw),
        precioFertilizantes: parseFormValue(fertilizantesRaw),
        precioSemillas: parseFormValue(semillasRaw),
        precioLabores: parseFormValue(laboresRaw),
        precioSeguros: parseFormValue(segurosRaw),
        precioAlquiler: parseFormValue(alquilerRaw),
        gastoDeCosecha: parseFormValue(gastoCosechaRaw),
      }),
    [
      precioCerealRaw,
      cotizacionDolarRaw,
      rindeRaw,
      agroquimicosRaw,
      fertilizantesRaw,
      semillasRaw,
      laboresRaw,
      segurosRaw,
      alquilerRaw,
      gastoCosechaRaw,
    ],
  );

  const hasRinde = parseFormValue(rindeRaw) > 0;

  const gastosImplantacionDisplay = calc.costoImplantacionUsd > 0
    ? `U$S ${fmtDisplay(calc.costoImplantacionUsd)}`
    : "Esperando datos";

  const inversionDisplay = calc.inversionTotal > 0
    ? `U$S ${fmtDisplay(calc.inversionTotal)}`
    : "Esperando datos";

  const gananciaDisplay = hasRinde
    ? fmt(calc.ganancia)
    : "Esperando datos";

  const rentabilidadDisplay = hasRinde
    ? fmt(calc.rentabilidad)
    : "Esperando datos";

  return (
    <Flex flexDir="column" gap="16px" paddingTop="16px" paddingBottom="24px">
      <Flex gap="32px">
        <Text fontWeight={600} fontSize="13px" color={AppColors.GRAY_TEXT}>
          {granoNombre} - {establecimiento} - {tipoCampo}
        </Text>
        <Text fontSize="13px" color={AppColors.GRAY_TEXT}>
          Rinde indicado por el productor:{" "}
          <strong>{rindeIndicado} qq/ha</strong>
        </Text>
      </Flex>

      <Grid templateColumns="repeat(2, 1fr)" gap={4}>
        <GridItem>
          <CurrencyFormField
            name={`${prefix}.cotizacionDolar`}
            label="Cotización Dólar"
            control={control}
            disabled={disabled}
            prefix="$ "
          />
        </GridItem>
        <GridItem>
          <CurrencyFormField
            name={`${prefix}.precioCereal`}
            label="Precio Cereal Neto"
            control={control}
            disabled={disabled}
            prefix="$ "
          />
        </GridItem>
      </Grid>

      <SectionDividerLabel label="Rindes" />
      <Grid templateColumns="repeat(3, 1fr)" gap={4}>
        <GridItem>
          <NumericCommaFormField
            name={`${prefix}.rindeEsperado`}
            label="Rinde esperado (qq/ha)"
            control={control}
            disabled={disabled}
          />
        </GridItem>
      </Grid>

      <SectionDividerLabel label="Implantación" />
      <Grid templateColumns="repeat(5, 1fr)" gap={3}>
        <GridItem>
          <CurrencyFormField
            name={`${prefix}.precioAgroquimicos`}
            label="Agroquímicos"
            control={control}
            disabled={disabled}
          />
        </GridItem>
        <GridItem>
          <CurrencyFormField
            name={`${prefix}.precioFertilizantes`}
            label="Fertilizante"
            control={control}
            disabled={disabled}
          />
        </GridItem>
        <GridItem>
          <CurrencyFormField
            name={`${prefix}.precioSemillas`}
            label="Semillas"
            control={control}
            disabled={disabled}
          />
        </GridItem>
        <GridItem>
          <CurrencyFormField
            name={`${prefix}.precioLabores`}
            label="Labores"
            control={control}
            disabled={disabled}
          />
        </GridItem>
        <GridItem>
          <CurrencyFormField
            name={`${prefix}.precioSeguros`}
            label="Seguro"
            control={control}
            disabled={disabled}
          />
        </GridItem>
      </Grid>

      <Grid templateColumns="repeat(2, 1fr)" gap={4}>
        <GridItem>
          <WaitingField
            label="Costo de Implantación"
            value={gastosImplantacionDisplay}
          />
        </GridItem>
      </Grid>

      <SectionDividerLabel label="Otros Gastos" />
      <Grid templateColumns="repeat(3, 1fr)" gap={4}>
        <GridItem>
          <CurrencyFormField
            name={`${prefix}.precioAlquiler`}
            label="Alquiler"
            control={control}
            disabled={disabled || tipoCampo === TipoCampoDisplay.Propio}
          />
        </GridItem>
        <GridItem>
          <NumericCommaFormField
            name={`${prefix}.gastoDeCosecha`}
            label="Gasto de cosecha (%)"
            control={control}
            disabled={disabled}
          />
        </GridItem>
      </Grid>

      <Grid templateColumns="repeat(2, 1fr)" gap={4}>
        <GridItem colSpan={2}>
          <WaitingField
            label="Inversión Total"
            value={inversionDisplay}
          />
        </GridItem>
      </Grid>

      <SectionDividerLabel label="Resultados" />
      <Grid templateColumns="repeat(2, 1fr)" gap={4}>
        <GridItem>
          <WaitingField label="Rentabilidad (%)" value={rentabilidadDisplay} />
        </GridItem>
        <GridItem>
          <WaitingField label="Ganancia (qq/ha)" value={gananciaDisplay} />
        </GridItem>
      </Grid>
    </Flex>
  );
};
